import axios from 'axios';
import { ApiResponse } from '../types';

// Determine API base URL. Prefer same-origin to ensure first-party cookies in PWAs
const resolveBaseURL = (): string => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && envUrl.trim().length > 0) return envUrl;
  try {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      return `${window.location.origin}/api/v1`;
    }
  } catch {}
  return '/api/v1';
};

// Create axios instance with default configuration
const api = axios.create({
  baseURL: resolveBaseURL(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
const requestQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = [];

const setAuthHeader = (config: any, token?: string) => {
  const authToken = token || localStorage.getItem('authToken');
  if (authToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: any) => setAuthHeader(config),
  (error: any) => Promise.reject(error)
);

const doRefresh = async (): Promise<string> => {
  const endpoint = `${resolveBaseURL()}/auth/refresh`;
  const resp = await axios.post<ApiResponse>(endpoint, {}, { withCredentials: true, timeout: 5000 });
  if (!resp.data?.success || !resp.data?.data?.token) {
    throw new Error('Refresh failed');
  }
  const newToken = resp.data.data.token as string;
  try { localStorage.setItem('authToken', newToken); } catch {}
  return newToken;
};

const enqueueRequest = () => new Promise<string>((resolve, reject) => requestQueue.push({ resolve, reject }));
const resolveQueue = (token: string) => { while (requestQueue.length) requestQueue.shift()!.resolve(token); };
const rejectQueue = (err: any) => { while (requestQueue.length) requestQueue.shift()!.reject(err); };

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response: any) => response,
  async (error: any) => {
    const originalRequest = error.config;
    const status = error?.response?.status;

    if (status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;

      try {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = doRefresh()
            .then((token) => {
              resolveQueue(token);
              return token;
            })
            .catch((err) => {
              rejectQueue(err);
              throw err;
            })
            .finally(() => {
              isRefreshing = false;
              refreshPromise = null;
            });
        }

        const token = await (refreshPromise ?? enqueueRequest());
        setAuthHeader(originalRequest, token);
        return api.request(originalRequest);
      } catch (refreshErr) {
        try { localStorage.removeItem('authToken'); } catch {}
        if (window.location.pathname !== '/login') {
          window.dispatchEvent(new CustomEvent('tokenExpired'));
        }
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  }
);

// API service methods
export const apiService = {
  // Auth endpoints
  auth: {
    googleLogin: (credential: string) => 
      api.post<ApiResponse>('/auth/google', { credential }),
    
    logout: () => 
      api.post<ApiResponse>('/auth/logout'),
    
    getProfile: (config?: any) => 
      api.get<ApiResponse>('/auth/profile', config),

    // Explicit refresh endpoint to allow silent refresh on startup
    refresh: (config?: { timeout?: number }) => {
      const endpoint = (process.env.REACT_APP_API_URL || 'https://family-planner-backend-971626684478.europe-north1.run.app/api/v1') + '/auth/refresh';
      return axios.post<ApiResponse>(endpoint, {}, { withCredentials: true, timeout: config?.timeout ?? 3000 });
    },
  },

  // User endpoints
  users: {
    getProfile: () => 
      api.get<ApiResponse>('/users/profile'),
    
    updateProfile: (data: any) => 
      api.patch<ApiResponse>('/users/profile', data),
    
    updatePreferences: (data: any) => 
      api.patch<ApiResponse>('/users/preferences', data),
    
    deleteAccount: () => 
      api.delete<ApiResponse>('/users/account'),
    
    exportData: () => 
      api.post<ApiResponse>('/users/export-data'),
  },

  // Family endpoints
  families: {
    getAll: () => 
      api.get<ApiResponse>('/families'),
    
    create: (data: any) => 
      api.post<ApiResponse>('/families', data),
    
    getById: (id: string) => 
      api.get<ApiResponse>(`/families/${id}`),
    
    update: (id: string, data: any) => 
      api.patch<ApiResponse>(`/families/${id}`, data),
    
    leave: (id: string) => 
      api.delete<ApiResponse>(`/families/${id}/leave`),
    
    inviteMember: (id: string, data: any) => 
      api.post<ApiResponse>(`/families/${id}/invite`, data),
    
    getInvitations: (id: string) => 
      api.get<ApiResponse>(`/families/${id}/invitations`),

    // Optional: include status query param: 'pending' | 'accepted' | 'expired' | 'all'
    getInvitationsByStatus: (id: string, status: 'pending' | 'accepted' | 'expired' | 'all') =>
      api.get<ApiResponse>(`/families/${id}/invitations`, { params: { status } }),
    
    cancelInvitation: (familyId: string, invitationId: string) => 
      api.delete<ApiResponse>(`/families/${familyId}/invitations/${invitationId}`),

    resendInvitation: (familyId: string, invitationId: string) =>
      api.post<ApiResponse>(`/families/${familyId}/invitations/${invitationId}/resend`, {}),

    deleteInvitation: (familyId: string, invitationId: string) =>
      api.delete<ApiResponse>(`/families/${familyId}/invitations/${invitationId}/hard-delete`),
    
    acceptInvitation: (token: string) => 
      api.post<ApiResponse>(`/families/invite/${token}`),
    
    updateMemberRole: (familyId: string, memberId: string, data: any) => 
      api.patch<ApiResponse>(`/families/${familyId}/members/${memberId}/role`, data),
    
    removeMember: (familyId: string, memberId: string) => 
      api.delete<ApiResponse>(`/families/${familyId}/members/${memberId}`),
    
    getMembers: (familyId: string) => 
      api.get<ApiResponse>(`/families/${familyId}/members`),
  },

  // Meal plan endpoints
  mealPlans: {
    getAll: (familyId: string, active?: boolean) => 
      api.get<ApiResponse>(`/families/${familyId}/meal-plans${active !== undefined ? `?active=${active}` : ''}`),
    
    getCurrent: (familyId: string) =>
      api.get<ApiResponse>(`/families/${familyId}/meal-plans/current`),

    getAvailableWeeks: (familyId: string) =>
      api.get<ApiResponse>(`/families/${familyId}/meal-plans/available-weeks`),

    getById: (familyId: string, mealPlanId: string) => 
      api.get<ApiResponse>(`/families/${familyId}/meal-plans/${mealPlanId}`),
    
    create: (familyId: string, data: any) => 
      api.post<ApiResponse>(`/families/${familyId}/meal-plans`, data),
    
    update: (familyId: string, mealPlanId: string, data: any) => 
      api.put<ApiResponse>(`/families/${familyId}/meal-plans/${mealPlanId}`, data),
    
    delete: (familyId: string, mealPlanId: string) => 
      api.delete<ApiResponse>(`/families/${familyId}/meal-plans/${mealPlanId}`),
    
    updateSlot: (
      familyId: string,
      mealPlanId: string,
      data: { dayOfWeek: number; mealType: 'lunch' | 'dinner'; type: 'recipe' | 'custom' | 'eating-out' | 'not-set'; recipeId?: string; customMeal?: string; eatingOutNote?: string; notes?: string }
    ) => api.put<ApiResponse>(`/families/${familyId}/meal-plans/${mealPlanId}/slots`, data),

    addMeal: (familyId: string, mealPlanId: string, data: any) =>
      api.post<ApiResponse>(`/families/${familyId}/meal-plans/${mealPlanId}/meals`, data),

    removeMeal: (familyId: string, mealPlanId: string, mealId: string) =>
      api.delete<ApiResponse>(`/families/${familyId}/meal-plans/${mealPlanId}/meals/${mealId}`),

    // AI Generation endpoints
    getPreferences: (familyId: string) =>
      api.get<ApiResponse>(`/families/${familyId}/meal-plan-preferences`),

    updatePreferences: (familyId: string, data: {
      mealSlots?: Record<string, boolean>;
      dietaryRestrictions?: string;
      additionalNotes?: string;
      noveltyLevel?: number;
    }) =>
      api.put<ApiResponse>(`/families/${familyId}/meal-plan-preferences`, data),

    generate: (familyId: string, data: {
      selectedSlots: Array<{ dayOfWeek: number; mealType: 'lunch' | 'dinner' }>;
      dietaryRestrictions?: string;
      additionalNotes?: string;
      noveltyLevel?: number;
    }) =>
      api.post<ApiResponse>(
        `/families/${familyId}/meal-plans/generate`,
        data,
        { timeout: parseInt(process.env.REACT_APP_AI_TIMEOUT_MS || '45000', 10) }
      ),

    generateSingle: (familyId: string, data: {
      slot: { dayOfWeek: number; mealType: 'lunch' | 'dinner' };
      dietaryRestrictions?: string;
      additionalNotes?: string;
      noveltyLevel?: number;
      excludeSuggestions?: string[];
    }) =>
      api.post<ApiResponse>(
        `/families/${familyId}/meal-plans/generate-single`,
        data,
        { timeout: parseInt(process.env.REACT_APP_AI_TIMEOUT_MS || '30000', 10) }
      ),

    finalize: (familyId: string, data: {
      weekNumber: number;
      year: number;
      meals: Array<{
        dayOfWeek: number;
        mealType: 'lunch' | 'dinner';
        type: 'existing-recipe' | 'new-suggestion' | 'eating-out' | 'not-set';
        recipeId?: string;
        recipeName?: string;
        suggestionName?: string;
        suggestionDescription?: string;
        eatingOutNote?: string;
      }>;
    }) =>
      api.post<ApiResponse>(
        `/families/${familyId}/meal-plans/finalize`,
        data,
        { timeout: 120000 } // 2 minutes for generating multiple recipes
      ),
  },

  // Recipe endpoints
  recipes: {
    getAll: (familyId: string, search?: string, cuisine?: string, difficulty?: string, tags?: string) => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (cuisine) params.append('cuisine', cuisine);
      if (difficulty) params.append('difficulty', difficulty);
      if (tags) params.append('tags', tags);
      
      return api.get<ApiResponse>(`/families/${familyId}/recipes${params.toString() ? `?${params.toString()}` : ''}`);
    },
    
    getById: (recipeId: string) => 
      api.get<ApiResponse>(`/recipes/${recipeId}`),
    
    create: (familyId: string, data: any) => 
      api.post<ApiResponse>(`/families/${familyId}/recipes`, data),
    
    update: (recipeId: string, data: any) => 
      api.put<ApiResponse>(`/recipes/${recipeId}`, data),
    
    delete: (recipeId: string) => 
      api.delete<ApiResponse>(`/recipes/${recipeId}`),
    
    import: (familyId: string, url: string) => 
      api.post<ApiResponse>(`/families/${familyId}/recipes/import`, { url }),

    generate: (
      familyId: string,
      params: {
        userDescription: string;
        type?: 'Vegetarisk' | 'Pescetarisk' | 'Kött' | 'IBS-vänlig';
        mainCarb?: 'Lågkolhydrat' | 'Ris' | 'Potatis' | 'Pasta';
        cuisine?: string;
        difficulty?: 'easy' | 'medium' | 'hard';
        timeRange?: '< 30 min' | '30–60 min' | '> 60 min';
        specialDiet?: 'Glutenfri' | 'Laktosfri' | 'Nötfri';
      }
    ) => api.post<ApiResponse>(
      `/families/${familyId}/recipes/generate`,
      params,
      { timeout: parseInt(process.env.REACT_APP_AI_TIMEOUT_MS || '30000', 10) }
    ),

    modifyWithAi: (
      familyId: string,
      recipeId: string,
      messages: Array<{ role: 'user' | 'assistant'; content: string }>
    ) => api.post<ApiResponse>(
      `/families/${familyId}/recipes/${recipeId}/modify`,
      { messages },
      { timeout: parseInt(process.env.REACT_APP_AI_TIMEOUT_MS || '30000', 10) }
    ),

    modifySuggestionWithAi: (
      familyId: string,
      current: any,
      messages: Array<{ role: 'user' | 'assistant'; content: string }>
    ) => api.post<ApiResponse>(
      `/families/${familyId}/recipes/generate/modify`,
      { current, messages },
      { timeout: parseInt(process.env.REACT_APP_AI_TIMEOUT_MS || '30000', 10) }
    ),

    checkDuplicate: (familyId: string, recipeContent: any) =>
      api.post<ApiResponse>(`/families/${familyId}/recipes/check-duplicate`, recipeContent),

    sendEmail: (recipeId: string, payload: { recipientEmail: string; recipientName?: string }) =>
      api.post<ApiResponse>(`/recipes/${recipeId}/send-email`, payload),

    getRecipients: () =>
      api.get<ApiResponse>(`/recipes/me/recipients`),

    getShareUrl: (recipeId: string) =>
      api.post<ApiResponse>(`/recipes/${recipeId}/share`),

    revokeShareUrl: (recipeId: string) =>
      api.delete<ApiResponse>(`/recipes/${recipeId}/share`),

    getSharedRecipe: (shareToken: string) =>
      axios.get<ApiResponse>(`${resolveBaseURL()}/recipes/public/shared/${shareToken}`),
  },

  // Shopping list endpoints
  shoppingList: {
    getForFamily: (familyId: string) => 
      api.get<ApiResponse>(`/shoppinglist/family/${familyId}`),
    
    getCompletedItems: (familyId: string) => 
      api.get<ApiResponse>(`/shoppinglist/family/${familyId}/completed`),
    
    addItem: (familyId: string, data: any) => 
      api.post<ApiResponse>(`/shoppinglist/family/${familyId}/items`, data),
    addBulkItems: (familyId: string, items: any[]) =>
      api.post<ApiResponse>(`/shoppinglist/family/${familyId}/items/bulk`, { items }),
    
    updateItem: (familyId: string, itemId: string, data: any) => 
      api.put<ApiResponse>(`/shoppinglist/family/${familyId}/items/${itemId}`, data),
    
    toggleItem: (familyId: string, itemId: string) => 
      api.patch<ApiResponse>(`/shoppinglist/family/${familyId}/items/${itemId}/toggle`),
    
    removeItem: (familyId: string, itemId: string) => 
      api.delete<ApiResponse>(`/shoppinglist/family/${familyId}/items/${itemId}`),
    
    updateOrder: (familyId: string, items: any[]) => 
      api.put<ApiResponse>(`/shoppinglist/family/${familyId}/items/order`, { items }),
    
    completeList: (familyId: string) => 
      api.patch<ApiResponse>(`/shoppinglist/family/${familyId}/complete`),
    
    getHistory: (familyId: string, params?: any) => 
      api.get<ApiResponse>(`/shoppinglist/family/${familyId}/history`, { params }),
  },

  // Chore endpoints
  chores: {
    getAll: (familyId: string) =>
      api.get<ApiResponse>(`/families/${familyId}/chores`),

    create: (familyId: string, data: any) =>
      api.post<ApiResponse>(`/families/${familyId}/chores`, data),

    getById: (familyId: string, id: string) =>
      api.get<ApiResponse>(`/families/${familyId}/chores/${id}`),

    update: (familyId: string, id: string, data: any) =>
      api.patch<ApiResponse>(`/families/${familyId}/chores/${id}`, data),

    delete: (familyId: string, id: string) =>
      api.delete<ApiResponse>(`/families/${familyId}/chores/${id}`),

    markDone: (familyId: string, id: string, instancePeriod?: { start: Date; end: Date }) =>
      api.patch<ApiResponse>(`/families/${familyId}/chores/${id}/mark-done`, instancePeriod ? { instancePeriod } : {}),

    markInProgress: (familyId: string, id: string, instancePeriod?: { start: Date; end: Date }) =>
      api.patch<ApiResponse>(`/families/${familyId}/chores/${id}/in-progress`, instancePeriod ? { instancePeriod } : {}),

    confirm: (familyId: string, id: string, status: 'CONFIRMED' | 'MISSED', confirmedAt?: string, instancePeriod?: { start: Date; end: Date }) =>
      api.patch<ApiResponse>(`/families/${familyId}/chores/${id}/confirm`, { status, confirmedAt, instancePeriod }),

    getHistory: (familyId: string, filters?: any) =>
      api.get<ApiResponse>(`/families/${familyId}/chores/history`, { params: filters }),

    deleteHistory: (familyId: string, historyId: string) =>
      api.delete<ApiResponse>(`/families/${familyId}/chores/history/${historyId}`),

    getScore: (familyId: string) =>
      api.get<ApiResponse>(`/families/${familyId}/chores/score`),

    getLeaderboard: (familyId: string) =>
      api.get<ApiResponse>(`/families/${familyId}/chores/leaderboard`),

    getActiveResponsibilities: (familyId: string) =>
      api.get<ApiResponse>(`/families/${familyId}/chores/responsibilities/active`),

    getUpcomingOperations: (familyId: string) =>
      api.get<ApiResponse>(`/families/${familyId}/chores/operations/upcoming`),

    // New endpoints
    getSummary: (familyId: string) =>
      api.get<ApiResponse>(`/families/${familyId}/chores/summary`),

    getWorkloadBalance: (familyId: string) =>
      api.get<ApiResponse>(`/families/${familyId}/chores/workload`),

    getAchievements: (familyId: string) =>
      api.get<ApiResponse>(`/families/${familyId}/chores/achievements`),

    getRecentAchievements: (familyId: string) =>
      api.get<ApiResponse>(`/families/${familyId}/chores/achievements/recent`),

    // Archive
    archive: (familyId: string, id: string) =>
      api.patch<ApiResponse>(`/families/${familyId}/chores/${id}/archive`),

    restore: (familyId: string, id: string) =>
      api.patch<ApiResponse>(`/families/${familyId}/chores/${id}/restore`),

    getArchived: (familyId: string) =>
      api.get<ApiResponse>(`/families/${familyId}/chores/archived`),

    // Comments
    addComment: (familyId: string, id: string, text: string, instancePeriod?: { start: Date; end: Date }) =>
      api.post<ApiResponse>(`/families/${familyId}/chores/${id}/comments`, { text, instancePeriod }),

    // Templates
    getTemplates: (familyId: string) =>
      api.get<ApiResponse>(`/families/${familyId}/chores/templates`),

    createFromTemplate: (familyId: string, templateId: string, data: any) =>
      api.post<ApiResponse>(`/families/${familyId}/chores/from-template/${templateId}`, data),

    createTemplate: (familyId: string, data: any) =>
      api.post<ApiResponse>(`/families/${familyId}/chores/templates`, data),

    // Bulk operations
    bulkMarkDone: (familyId: string, choreIds: string[]) =>
      api.patch<ApiResponse>(`/families/${familyId}/chores/bulk/mark-done`, { choreIds }),

    bulkReassign: (familyId: string, choreIds: string[], newAssignee: string) =>
      api.patch<ApiResponse>(`/families/${familyId}/chores/bulk/reassign`, { choreIds, newAssignee }),

    bulkArchive: (familyId: string, choreIds: string[]) =>
      api.patch<ApiResponse>(`/families/${familyId}/chores/bulk/archive`, { choreIds }),
  },

  // Admin endpoints
  admin: {
    getAllFamilies: () => 
      api.get<ApiResponse>('/admin/families'),
    
    getFamilyById: (id: string) => 
      api.get<ApiResponse>(`/admin/families/${id}`),
    
    archiveFamily: (id: string) => 
      api.patch<ApiResponse>(`/admin/families/${id}/archive`),
    
    restoreFamily: (id: string) => 
      api.patch<ApiResponse>(`/admin/families/${id}/restore`),
    
    deleteFamily: (id: string) => 
      api.delete<ApiResponse>(`/admin/families/${id}`),
    
    getAnalytics: () => 
      api.get<ApiResponse>('/admin/analytics'),
  },

  // Notification endpoints
  notifications: {
    getAll: (params?: { page?: number; limit?: number; unreadOnly?: boolean }) => {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.unreadOnly) queryParams.append('unreadOnly', params.unreadOnly.toString());
      
      return api.get<ApiResponse>(`/notifications${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
    },
    
    markAsRead: (id: string) => 
      api.patch<ApiResponse>(`/notifications/${id}/read`),
    
    markAllAsRead: () => 
      api.patch<ApiResponse>('/notifications/mark-all-read'),
    
    delete: (id: string) => 
      api.delete<ApiResponse>(`/notifications/${id}`),
    
    updatePreferences: (data: any) => 
      api.post<ApiResponse>('/notifications/preferences', data),
    
    // Public notification endpoints
    getVapidPublicKey: () => 
      api.get<ApiResponse>('/notifications/public/vapid-public-key'),
    
    getCurrentSubscription: () => 
      api.get<ApiResponse>('/notifications/subscription'),
    
    subscribe: (subscription: any) => 
      api.post<ApiResponse>('/notifications/subscribe', { subscription }),
    
    unsubscribe: () => 
      api.post<ApiResponse>('/notifications/unsubscribe'),
    
    test: () => 
      api.post<ApiResponse>('/notifications/test'),
    
    triggerScheduled: () => 
      api.post<ApiResponse>('/notifications/trigger-scheduled'),
  },
};

export default apiService; 